import {
  useModalContext,
  WSButton,
  WSText
} from "@wingspanhq/fe-component-library";
import { VerificationStatus } from "@wingspanhq/payments/dist/interfaces";
import {
  AccountStatus,
  AccountType,
  IAccount
} from "@wingspanhq/users/dist/lib/interfaces";
import React, { useState } from "react";
import { SetupFlowRouteComponentProps } from "../../../components/SetupFlow/SetupFlow";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { QUERY_PAYMENTS_STATUS } from "../../../query/payments/keys";
import { useCreateAccount } from "../../../query/users/mutations";
import { useAccounts } from "../../../query/users/queries";
import { paymentsService } from "../../../services/payments";
import { useMemberPlaidLink } from "../../../utils/usePlaidLink";
import {
  ChooseAccountForPayoutsModal,
  CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL
} from "../../components/ChooseAccountForPayoutsModal/ChooseAccountForPayoutsModal";
import { OnboardingOption } from "../../components/OnboardingOption/OnboardingOption";
import { PaymentsSetupLayout } from "../../components/PaymentsSetupLayout/PaymentsSetupLayout";

type BankAccountConnectType = "plaid" | "manually";

export const SetupPayoutsConnectBank: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();
  const { openModal, closeModal } = useModalContext();
  const [connectType, setConnectType] = useState<
    BankAccountConnectType | undefined
  >();
  const [createAccount, createAccountMeta] = useCreateAccount();
  const accountsQuery = useAccounts();
  const featureFlagsQuery = useFeatureFlags();

  const handleChange = (event: any) => {
    setConnectType(event.target.value);
  };

  const [submit, submitMeta] = useWSMutation(
    async () => {
      if (!featureFlagsQuery.data?.verificationsByFeature) {
        await paymentsService.service.update(userId, {
          verifications: {
            taxDocumentation: VerificationStatus.Verified,
            banking: VerificationStatus.Verified,
            cardPayments: VerificationStatus.Verified
          }
        });
      }

      return { connectType };
    },
    {
      dependencies: [QUERY_PAYMENTS_STATUS],
      onSuccess: onNext
    }
  );

  const plaidHandler = useMemberPlaidLink({
    onSuccess: async (publicToken: string) => {
      const accounts = (await createAccount({
        publicToken,
        status: AccountStatus.Pending
      })) as IAccount[];

      if (accounts) {
        openModal(CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL);

        return {
          success: true,
          data: accounts
        };
      } else {
        return {
          success: false
        };
      }
    }
  });

  return (
    <PaymentsSetupLayout onBack={onBack} title="Connect your bank account">
      <OnboardingOption
        mb="S"
        name="connectType"
        value="plaid"
        onChange={handleChange}
        title="Connect Instantly"
      >
        <WSText>
          Log in with your bank account information and get paid with a direct
          bank transfer (ACH)
        </WSText>
      </OnboardingOption>

      <OnboardingOption
        mb="S"
        name="connectType"
        value="manually"
        onChange={handleChange}
        title="Enter your bank account details"
      >
        <WSText>Manually enter your and account numbers</WSText>
      </OnboardingOption>

      <WSButton
        mt="M"
        name="continue"
        onClick={() => {
          if (connectType === "manually") {
            submit();
          } else {
            const accounts = accountsQuery.data || [];

            const payoutAccounts = accounts.filter(
              account =>
                account.type === AccountType.Depository &&
                account.canBeUsedFor?.payouts
            );

            if (payoutAccounts.length > 0) {
              openModal(CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL);
            } else {
              plaidHandler.open();
            }
          }
        }}
        loading={submitMeta.isLoading || createAccountMeta.isLoading}
        disabled={!connectType}
        fullWidth
      >
        Continue
      </WSButton>

      <WSText.Label mt="M" color="grey600">
        Wingspan partners with Plaid to verify your bank account. Plaid is
        certified in internationally-recognized security standards, like ISO
        27001, ISO 27701, and is SSAE18 SOC 2 compliant.
      </WSText.Label>

      <ChooseAccountForPayoutsModal
        onSuccess={() => {
          closeModal(CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL);
          submit();
        }}
      />
    </PaymentsSetupLayout>
  );
};
